/**
 * The agency's mark at the head of a document.
 *
 * **One component, because a letterhead is the thing that drifts.** An invoice, a receipt, a
 * landlord statement and a buyer's statement are written months apart by different hands, and
 * four hand-rolled headers become four different sizes, four different fallbacks and one of them
 * quietly missing the logo altogether. Everything that produces paper for a client renders this,
 * so the treatment is decided once.
 *
 * **An agency with no mark must look deliberate, not unfinished.** There is no placeholder box
 * and no grey square: with nothing uploaded this renders exactly the name treatment these
 * documents have always carried, and a reader has no way of telling that a feature exists.
 *
 * **A mark that fails to load must not leave a broken square on a client's receipt.** The image
 * is torn out on its first `error` and the name treatment takes over — which covers the file
 * having been removed from storage, a proxy in the way, and the payer being offline with the
 * rest of the page already cached. Falling back is silent on purpose: the person holding the
 * receipt cannot fix it and should not be told about it.
 *
 * `logoUrl` is passed in rather than read from the session, so this works unchanged on a public
 * document, where there is no session to read — the pay page a stranger opens, and the client
 * portal, where the session is the tenant's and carries the agency's mark on `/portal/me`.
 */

import { useEffect, useState, type ReactNode, type SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/**
 * Icons, drawn here rather than imported.
 *
 * Same construction as `components/icons.tsx` — 24 grid, 1.75 stroke, round caps and joins,
 * `currentColor` — so they sit with the rest of the set. They live beside the one screen and the
 * one component that use them.
 */
function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** A sheet of paper with a mark in its corner — which is exactly what this feature is. */
export const IconAgencyMark = (props: IconProps) => (
  <Icon {...props}>
    <path d="M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
    <path d="M14 3v5h5" />
    <rect x="7.5" y="12" width="5" height="5" rx="1.25" />
    <path d="M15 16.5h2" />
    <path d="M15 19.5h2" />
  </Icon>
);

/** A file going up into a tray. */
export const IconUpload = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 15V4" />
    <path d="m8 8 4-4 4 4" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </Icon>
);

export interface AgencyMarkProps {
  /** The agency's registered name. Always shown — a mark is not a substitute for it on paperwork. */
  name: string;
  /**
   * Where the mark is served, or null.
   *
   * A root-relative path written by the upload endpoint; see `orgs.logo_url` in
   * `apps/api/src/routes/settings.ts` for why the column holds a path rather than an id.
   */
  logoUrl?: string | null;
  /**
   * `document` for a sheet somebody prints and hands over; `compact` where the header is a single
   * line, as on the landlord statement; `header` for a navigation bar, where the mark and the name
   * sit side by side on one line rather than stacked.
   */
  size?: "document" | "compact" | "header";
  /** The address, the telephone number — whatever that particular document puts under the name. */
  children?: ReactNode;
}

const MARK_CLASS = {
  /* Tall enough to be a letterhead, short enough that a wide wordmark still leaves room for the
     invoice number opposite it. Height is capped and the width follows, so a tall mark and a wide
     one both land on the same baseline. */
  document: "mb-2 max-h-14 max-w-[13rem]",
  compact: "mb-2 max-h-9 max-w-[13rem]",
  /* Shorter and narrower than either, because this one shares a bar with the reader's own
     navigation on a telephone. Both caps are load-bearing: an agency whose mark is a tall square
     would double the height of every screen in the portal, and one whose mark is a wide wordmark
     would push the name off the end of it. */
  header: "max-h-8 max-w-[9rem]",
} as const;

const NAME_CLASS = {
  document: "text-lg font-semibold text-brand-900",
  compact: "text-sm font-semibold text-brand-900",
  header: "truncate text-base font-semibold text-brand-900",
} as const;

/* The two stacked treatments read down the page, the way a letterhead does. A bar reads across. */
const WRAP_CLASS = {
  document: "",
  compact: "",
  header: "flex min-w-0 items-center gap-2.5",
} as const;

export function AgencyMark({ name, logoUrl, size = "document", children }: AgencyMarkProps) {
  /* Reset when the address changes, so replacing a mark that had failed shows the new one rather
     than staying torn out for the life of the page. */
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [logoUrl]);

  const showMark = Boolean(logoUrl) && !broken;

  return (
    <div className={WRAP_CLASS[size]}>
      {showMark ? (
        <img
          src={logoUrl as string}
          /* Empty, and not the agency's name: the name is the very next element, and a screen
             reader announcing it twice is worse than not announcing the picture at all. */
          alt=""
          onError={() => setBroken(true)}
          className={`w-auto shrink-0 ${MARK_CLASS[size]} object-contain object-left`}
        />
      ) : null}
      <p className={NAME_CLASS[size]}>{name}</p>
      {children}
    </div>
  );
}
